import React from 'react';
import { Collapse as MantineCollapse, Divider as MantineDivider } from '@mantine/core';
import { isDefinedSome, isEmpty, truncate } from '@nfctron/nexus-core';
import { NoResultsIllustration } from '@nfctron/nexus-react-svg';
import { Slot } from '@radix-ui/react-slot';

import { type ExtendedComponentProps } from '../types';
import { UiTooltip, UiTooltipContent, UiTooltipTrigger } from '../ui';
import { cn } from '../utils';

/* -------------------------------------------------------------------------------------------------
 * Collapse
 * TODO: replace with custom solution
 * -----------------------------------------------------------------------------------------------*/
export interface CollapseProps extends ExtendedComponentProps<{}, typeof MantineCollapse, false> {}

export const Collapse: React.FunctionComponent<CollapseProps> = (props) => {
	const { children, ...rest } = props;
	return <MantineCollapse {...rest}>{children}</MantineCollapse>;
};
Collapse.displayName = 'Collapse';

/* -------------------------------------------------------------------------------------------------
 * Divider
 * TODO: replace with UiDivider
 * -----------------------------------------------------------------------------------------------*/
export interface DividerProps extends ExtendedComponentProps<{}, typeof MantineDivider> {}

export const Divider: React.FunctionComponent<DividerProps> = (props) => {
	const { ...rest } = props;
	return <MantineDivider {...rest} />;
};
Divider.displayName = 'Divider';

/* -------------------------------------------------------------------------------------------------
 * EmptyState
 * -----------------------------------------------------------------------------------------------*/
export interface EmptyStateProps
	extends ExtendedComponentProps<
		{
			withIllustration?: boolean;
			message?: React.ReactNode;
		},
		'div'
	> {}

export const EmptyState: React.FunctionComponent<EmptyStateProps> = (props) => {
	const { withIllustration, message, children, className, ...rest } = props;
	return (
		<div className={cn('w-full h-full max-w-full flex items-center justify-center flex-grow text-sm text-zinc-400', className)} {...rest}>
			<div className="flex flex-col gap-3 my-auto items-center text-center">
				{/* illustration */}
				{withIllustration && <NoResultsIllustration className="h-auto w-[85px]" />}
				{/* content */}
				{isDefinedSome(children, message) && (
					<div className="block font-normal">
						{!isEmpty(message) && <span className="block">{message}</span>}
						{children}
					</div>
				)}
			</div>
		</div>
	);
};
EmptyState.displayName = 'EmptyState';

/* -------------------------------------------------------------------------------------------------
 * Truncate
 * -----------------------------------------------------------------------------------------------*/
export interface TruncateProps
	extends ExtendedComponentProps<
		{
			children?: string;
			maxLength?: number;
			asChild?: boolean;
		},
		'span'
	> {}

export const Truncate: React.FunctionComponent<TruncateProps> = (props) => {
	const { maxLength = 150, asChild, children = '', className, ...rest } = props;
	const shouldEvenTruncate = children.length > maxLength;
	const Comp = asChild ? Slot : 'span';

	if (!shouldEvenTruncate) {
		return (
			<Comp className={cn(className)} {...props}>
				{children}
			</Comp>
		);
	}

	return (
		<UiTooltip>
			<UiTooltipTrigger asChild>
				<Comp className={cn('break-words whitespace-pre-line', className)} {...props}>
					{truncate(children, maxLength)}
				</Comp>
			</UiTooltipTrigger>
			<UiTooltipContent className="max-w-64 whitespace-pre-line break-words">
				<span className="text-sm">
					{'…'}
					{children.slice(maxLength - 1)}
				</span>
			</UiTooltipContent>
		</UiTooltip>
	);
};
Truncate.displayName = 'Truncate';
